/**
 * Date-range reconciliation between a report's build filter and a viewer's own
 * date filter on the same field.
 *
 * A build filter can pin a date field to a window (absolute gte/lte, or a
 * relative preset like "previous quarter" that resolves to concrete bounds).
 * The viewer's data and export are always AND-clamped to that window, so a
 * viewer date selection can only narrow it — but a relative preset the viewer
 * picks (e.g. "past 7 days") can resolve to a range that doesn't overlap the
 * window at all, silently yielding zero rows. These helpers compute the window
 * (to clamp the calendar pickers) and detect a non-overlapping selection (to
 * warn), so the viewer isn't left staring at an unexplained empty result.
 */

import { resolveRelativeDateFilters } from './relative-date-resolver';
import { parseFilterKey, type FilterLayout } from './filter-operators';

/** Inclusive date bounds as ISO `YYYY-MM-DD` strings; an absent bound is open (±∞). */
export interface DateRange {
  gte?: string;
  lte?: string;
}

/** The later of two ISO date strings (tightens a lower bound). */
function laterDate(a: string | undefined, b: string): string {
  return a === undefined || b > a ? b : a;
}

/** The earlier of two ISO date strings (tightens an upper bound). */
function earlierDate(a: string | undefined, b: string): string {
  return a === undefined || b < a ? b : a;
}

/**
 * Shifts plain YYYY-MM-DD values by whole UTC days, converting exclusive bounds to inclusive day
 * bounds. Other values, including full timestamps, pass through unchanged.
 */
function shiftIsoDay(iso: string, deltaDays: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  return new Date(Date.UTC(y, m - 1, d + deltaDays)).toISOString().slice(0, 10);
}

/**
 * The effective inclusive date range a set of filter values imposes on one
 * field. Relative presets are resolved to concrete bounds first, then every
 * operator on the field (eq, gt/gte, lt/lte — `between` is stored as gte+lte)
 * is folded into the tightest [gte, lte]. Returns null when the field carries
 * no date-range constraint. `ne`/`is_empty`/`is_not_empty` are not ranges and
 * are ignored.
 */
export function dateRangeForField(
  filterValues: Record<string, unknown>,
  fieldKey: string
): DateRange | null {
  const resolved = resolveRelativeDateFilters(filterValues);
  let gte: string | undefined;
  let lte: string | undefined;

  for (const [key, value] of Object.entries(resolved)) {
    if (value == null || value === '') continue;
    const { baseKey, operator } = parseFilterKey(key);
    if (baseKey !== fieldKey) continue;
    const v = String(value);
    switch (operator) {
      case 'eq':
        gte = laterDate(gte, v);
        lte = earlierDate(lte, v);
        break;
      case 'gte':
        gte = laterDate(gte, v);
        break;
      case 'gt':
        gte = laterDate(gte, shiftIsoDay(v, 1));
        break;
      case 'lte':
        lte = earlierDate(lte, v);
        break;
      case 'lt':
        lte = earlierDate(lte, shiftIsoDay(v, -1));
        break;
      // ne / is_empty / is_not_empty aren't ranges; ignore.
    }
  }

  if (gte === undefined && lte === undefined) return null;
  return { gte, lte };
}

/**
 * The build filter's date window for a field, or null when the field isn't
 * pinned by a hard bound. Only a top-level AND slot counts: a date constraint
 * inside an OR group is not a guaranteed floor, so it must not clamp or warn.
 * An empty layout means legacy flat-AND (every build value is top-level).
 */
export function buildDateWindowForField(
  buildFilterValues: Record<string, unknown>,
  buildFilterLayout: FilterLayout,
  fieldKey: string
): DateRange | null {
  if (buildFilterLayout.length > 0) {
    const isTopLevel = buildFilterLayout.some(
      (slot) => slot.type === 'filter' && slot.fieldKey === fieldKey
    );
    if (!isTopLevel) return null;
  }
  return dateRangeForField(buildFilterValues, fieldKey);
}

/** The later of two ISO date bounds, treating '' as "no bound". */
export function laterIsoDate(a: string, b: string): string {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

/** Whether two inclusive ranges overlap; an absent bound is treated as ±∞. */
export function dateRangesOverlap(a: DateRange, b: DateRange): boolean {
  if (a.gte !== undefined && b.lte !== undefined && a.gte > b.lte) return false;
  if (a.lte !== undefined && b.gte !== undefined && a.lte < b.gte) return false;
  return true;
}

/**
 * True when selection exceeds the inclusive report window; missing window bounds are open.
 * Exceeding ranges are narrowed or become empty if disjoint.
 */
export function dateSelectionExceedsWindow(selection: DateRange, window: DateRange): boolean {
  const exceedsLow =
    window.gte !== undefined && (selection.gte === undefined || selection.gte < window.gte);
  const exceedsHigh =
    window.lte !== undefined && (selection.lte === undefined || selection.lte > window.lte);
  return exceedsLow || exceedsHigh;
}

/** Human-readable window bounds for a warning message. */
export function dateWindowLabel(window: DateRange): string {
  if (window.gte && window.lte) return `${window.gte} – ${window.lte}`;
  if (window.gte) return `on or after ${window.gte}`;
  if (window.lte) return `on or before ${window.lte}`;
  return '';
}

/**
 * The warning to show a viewer whose date selection reaches outside the build
 * window — empty (no overlap) or capped (extends beyond it). Returns '' when
 * there's no window, no selection, or the selection sits fully inside the
 * window (they get exactly what they picked). Phrasing is subject-neutral so
 * both the tabular report viewer and the dashboard viewer can share it.
 */
export function dateWindowWarningMessage(
  selection: DateRange | null,
  window: DateRange | null
): string {
  if (!window || !selection) return '';
  const label = dateWindowLabel(window);
  if (!dateRangesOverlap(selection, window)) {
    return `This range is outside the allowed date window (${label}); no rows will match.`;
  }
  if (dateSelectionExceedsWindow(selection, window)) {
    return `Results are limited to the date window (${label}), so your selection is capped to it.`;
  }
  return '';
}
